import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {useState} from 'react';
import {useMemo} from 'react';
import {EventWizardDetails} from '@Components/event-wizard/event-wizard-details';
import {setReduxForEventPresetPrefill} from '@Components/event-wizard/event-wizard-preset-prefill.helper';
import {EventFooter} from '@Components/event-wizard/event-footer';
import {Preview} from '@Components/event-wizard/preview';
import {EventWizardReview} from '@Components/event-wizard/event-wizard-review';
import {EventWizardTicketing} from '@Components/event-wizard/event-wizard-ticketing';
import type {EventWizardComponentProps, EventWizardContainerQuery} from '@Components/event-wizard/event-wizard.types';
import {EventWizardPresentation, EventWizardProgressStep, EventWizardSourceType} from '@Components/event-wizard/event-wizard.types';
import {useDispatch} from 'react-redux';
import {
  getDefaultEventTeamShareDetails,
  getDefaultOrganiserDetails,
  getDefaultRegistrationDetails,
  onEventWizardAllTypesChange,
  onEventWizardDoesPAYGUserHaveAITemplateLimitUpdate,
  onEventWizardGoogleMapAPIKeyChange,
  onEventWizardOrganiserUpdate,
  onEventWizardIsEditingPublishedEventUpdate,
  onEventWizardPageSourceTypeUpdate,
  onEventWizardRegistrationChange,
  onEventWizardShareInTeamToggle,
  onEventWizardShouldUpsellRecurring,
  onEventWizardTeamNameUpdate,
  onEventWizardTicketingGatingPropsUpdate,
  onEventWizardRegistrationModeChange,
  onEventWizardUpdateShouldShowRecurringEventDetails,
  onEventWizardUserPastOfflineVenueChange,
} from '@Components/event-wizard/event-wizard-slice';
import {useEventWizardCurrentProgressStep, useEventWizardIsDraftBeingSaved, useEventWizardWizardContainerQuery} from '@Components/event-wizard/event-wizard.hooks';
import {markEventWizardContentAsSaved, setReduxForDesignPrefill, setReduxForStartDatePrefill, setReduxForUpdatingEvent} from '@Components/event-wizard/event-wizard.helper';
import {applyNewEventDefaultStyle} from '@Components/event-wizard/event-wizard-new-event-style.helper';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {EVENT_WIZARD_PRESENTATION_CONTEXT} from '@Components/event-wizard/event-wizard-presentation-context';
import {getDefaultRegistrationModeForNewEvent} from '@Components/event-wizard/event-wizard-ticketing/ticketing-gating.helper';
import {CircularProgressLoader} from '@Components/circular-progress-loader';
import styles from './event-wizard.module.scss';

export function EventWizard({
  googleMapAPIKey,
  organisers,
  types,
  teamName,
  teamSize,
  customRegisterBtnTexts,
  userPastVenues,
  eventWizardSourceType,
  eventVO,
  eventVOMinifiedWizardPrefill,
  latestEventStyle,
  doesPAYGUserHaveAITemplateLimit,
  shouldUpsellRecurring,
  shouldShowRecurringEventDetails,
  isTicketingCountrySupported,
  isPaidTicketingFeatureAvailable,
  notifyMeEmail,
  presentation = EventWizardPresentation.PAGE,
  nav,
  onEventSaved,
  onDraftSaved,
  onCancel,
  ga4Source,
}: EventWizardComponentProps): ReactElement<any> {
  const [isWizardReady, setIsWizardReady] = useState(false);
  const dispatch = useDispatch();
  const currentStep = useEventWizardCurrentProgressStep();
  const {containerQueryParamProps, containerRef, hasContainerQueryInitialized} = useEventWizardWizardContainerQuery();
  const isDraftBeingSaved = useEventWizardIsDraftBeingSaved();

  const eventWizardContainerQueriesContextValues: EventWizardContainerQuery = useMemo(() => {
    return {
      isXXsmallScreen: containerQueryParamProps.isXXsmallScreen,
      isExtraSmallScreen: containerQueryParamProps.isExtraSmallScreen,
      isSmallScreen: containerQueryParamProps.isSmallScreen,
      isMediumScreen: containerQueryParamProps.isMediumScreen,
      isLargeScreen: containerQueryParamProps.isLargeScreen,
      isExtraLargeScreen: containerQueryParamProps.isExtraLargeScreen,
    };
  }, [containerQueryParamProps]);

  useEffect(() => {
    setDefaultData();
    dispatch(onEventWizardGoogleMapAPIKeyChange(googleMapAPIKey));
    try {
      setDataByPageSourceType();
    } catch (error: unknown) {
      // Best-effort: the aim is never to land here, not to recover cleverly once we have. Hydration can fail
      // for reasons that have nothing to do with drafts, and falling back to a blank wizard is what the app
      // has always done -- settled as out of scope (T3.12), so this stays as it is. The throw still has to
      // surface, because the UI recovering quietly is exactly what hides it.
      console.error('Event wizard hydration failed', error);
      setDefaultData();
    }
    markEventWizardContentAsSaved();
    setIsWizardReady(true);
  }, []);

  const setDefaultData = (): void => {
    dispatch(onEventWizardUserPastOfflineVenueChange(userPastVenues));
    dispatch(onEventWizardAllTypesChange(types));
    dispatch(
      onEventWizardOrganiserUpdate({
        allOrganisers: organisers,
        selectedOrganiser: organisers.length > 0 ? organisers[0] : getDefaultOrganiserDetails().selectedOrganiser,
      })
    );
    dispatch(onEventWizardTeamNameUpdate(teamName ?? undefined));

    if (teamSize === null || teamSize === 0) {
      dispatch(onEventWizardShareInTeamToggle(false));
    } else {
      dispatch(onEventWizardShareInTeamToggle(getDefaultEventTeamShareDetails()));
    }

    dispatch(
      onEventWizardRegistrationChange({
        ...getDefaultRegistrationDetails(),
        customBtnTexts: customRegisterBtnTexts,
      })
    );
    dispatch(onEventWizardDoesPAYGUserHaveAITemplateLimitUpdate(doesPAYGUserHaveAITemplateLimit));
    dispatch(onEventWizardShouldUpsellRecurring(shouldUpsellRecurring));
    dispatch(onEventWizardUpdateShouldShowRecurringEventDetails(shouldShowRecurringEventDetails));
    dispatch(onEventWizardTicketingGatingPropsUpdate({isCountrySupported: isTicketingCountrySupported, isPaidTicketingAvailable: isPaidTicketingFeatureAvailable, notifyMeEmail}));
    dispatch(onEventWizardRegistrationModeChange(getDefaultRegistrationModeForNewEvent(isPaidTicketingFeatureAvailable, isTicketingCountrySupported)));
  };

  const setDataByPageSourceType = (): void => {
    const isUpdatingExistingEvent = eventWizardSourceType === EventWizardSourceType.UPDATE;

    dispatch(onEventWizardPageSourceTypeUpdate(eventWizardSourceType));
    dispatch(onEventWizardIsEditingPublishedEventUpdate(isUpdatingExistingEvent && eventVO?.isPublished === true));

    if (isUncolouredNewEvent()) {
      applyNewEventDefaultStyle(latestEventStyle);
    }

    if (
      eventWizardSourceType === EventWizardSourceType.DEFAULT ||
      eventWizardSourceType === EventWizardSourceType.UPDATE ||
      eventWizardSourceType === EventWizardSourceType.DUPLICATE
    ) {
      setReduxForUpdatingEvent(eventVO);
      return;
    }

    if (eventWizardSourceType === EventWizardSourceType.PREFILL_FROM_DESIGN) {
      setReduxForDesignPrefill(eventVOMinifiedWizardPrefill);
      return;
    }

    if (eventWizardSourceType === EventWizardSourceType.PREFILL_FROM_DATE_SELECTION) {
      setReduxForStartDatePrefill(eventVOMinifiedWizardPrefill);
      return;
    }

    if (eventWizardSourceType === EventWizardSourceType.PREFILL_FROM_GALLERY_CATEGORY) {
      setReduxForEventPresetPrefill(eventVOMinifiedWizardPrefill);
    }
  };

  /**
   * A new event that no preset colours: updates and duplicates bring their own style, and a gallery preset its own background.
   */
  const isUncolouredNewEvent = (): boolean => {
    return !eventVO && eventWizardSourceType !== EventWizardSourceType.PREFILL_FROM_GALLERY_CATEGORY;
  };

  const getBodyContent = (): ReactElement<any> => {
    if (eventWizardContainerQueriesContextValues.isSmallScreen) {
      return (
        <div className={`flex-column-align-start justify-content-center spacing-m-t-4 spacing-p-l-4 spacing-p-r-4 _full-width ${styles.wizardContentMaxWidth}`}>
          {currentStep === EventWizardProgressStep.OVERVIEW ? <EventWizardDetails /> : undefined}
          {currentStep === EventWizardProgressStep.TICKETING ? <EventWizardTicketing /> : undefined}
          {currentStep === EventWizardProgressStep.REVIEW ? <EventWizardReview /> : undefined}
        </div>
      );
    }

    return (
      <div className={`${styles.contentContainer} flex-row-align-center spacing-m-t-5 spacing-p-l-10 spacing-p-r-10`}>
        <div className={`${styles.content} spacing-m-r-10`}>
          {currentStep === EventWizardProgressStep.OVERVIEW ? <EventWizardDetails /> : undefined}
          {currentStep === EventWizardProgressStep.TICKETING ? <EventWizardTicketing /> : undefined}
          {currentStep === EventWizardProgressStep.REVIEW ? <EventWizardReview /> : undefined}
        </div>
        <Preview />
      </div>
    );
  };

  const isEmbedded = presentation === EventWizardPresentation.EMBEDDED;

  return (
    <div className={`${styles.pageContainer} ${isEmbedded ? styles.embedded : ''} flex-column-align-center`}>
      <EVENT_WIZARD_PRESENTATION_CONTEXT.Provider value={presentation}>
        <EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT.Provider value={eventWizardContainerQueriesContextValues}>
          <div ref={containerRef} className=" _full-width _full-height">
            {hasContainerQueryInitialized && isWizardReady ? (
              <div className="flex-column-align-center _full-height _full-width">
                {nav}
                <div className={`flex-column-align-center _full-height _full-width ${isDraftBeingSaved ? styles.dimmedWhileSaving : ''}`}>{getBodyContent()}</div>
              </div>
            ) : (
              <CircularProgressLoader classes="-for-mystuff-content" />
            )}
          </div>
          <EventFooter onEventSaved={onEventSaved} onDraftSaved={onDraftSaved} onCancel={onCancel} ga4Source={ga4Source} />
        </EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT.Provider>
      </EVENT_WIZARD_PRESENTATION_CONTEXT.Provider>
    </div>
  );
}
